<script setup lang="ts">
import type { HouseReview } from '~/types/house/types'

defineProps<{
  reviews: HouseReview[]
}>()

const { isMobile } = useBreakpoint()

const currentReview = ref<HouseReview | null>(null)
const showModal = ref(false)
const swiperOptions = {
  breakpoints: {
    768: {
      slidesPerView: 2,
      spaceBetween: 16,
    },
  },
  centeredSlides: false,
  loop: false,
  slidesPerView: 1.4,
  spaceBetween: 8,
}

const openAll = () => {
  currentReview.value = null
  showModal.value = true
}
const openMore = (review: HouseReview) => {
  currentReview.value = review
  showModal.value = true
}
</script>

<template>
  <div class="md:mb-9">
    <h2 class="mb-5 text-2xl md:mb-6">
      {{ $t('house.reviews.titleHouse') }}
    </h2>

    <HouseCustomerReview
      v-if="reviews.length === 1"
      :review="reviews[0]"
      class="rounded border border-gray-200 px-4 py-6 md:w-1/2"
      @click-more="openMore(reviews[0])"
    />

    <VirtualBaseCarousel
      v-else
      class="mb-6 mr-[-15px] pr-[15px] md:mr-0 md:pr-0"
      :navigation="!isMobile && reviews.length > 2"
      :pagination="!isMobile && reviews.length > 2"
      :slides="reviews"
      :swiper-options="swiperOptions"
      :virtual="false"
      swiper-slide-class="h-auto"
      class-name="house-customer-reviews__slider"
    >
      <template #slider-content="{ slide, slideIndex }">
        <HouseCustomerReview
          :key="`customer-review-${slideIndex}`"
          :review="slide as HouseReview"
          class="h-full rounded border border-gray-200 px-4 py-6"
          @click-more="openMore(slide as HouseReview)"
        />
      </template>

      <template #navigation>
        <VirtualBaseCarouselPaginationV2 v-if="!isMobile" class="mt-6" />
      </template>
    </VirtualBaseCarousel>

    <BaseButton
      v-if="
        (isMobile && reviews.length > 1) || (!isMobile && reviews.length > 2)
      "
      class="!text-md !font-bold"
      color="secondary"
      variant="outline"
      :block-full="isMobile"
      @click="openAll"
    >
      {{ $t('house.reviews.seeAllComments') }}
    </BaseButton>

    <BaseModalV2
      :model-value="showModal"
      size="s"
      active-esc-mode
      @close="showModal = false"
      @update:model-value="showModal = false"
    >
      <template #content>
        <div>
          <HouseCustomerReview
            v-if="currentReview"
            :active-truncate="false"
            :review="currentReview"
            class="mb-6 desktop:mt-16"
          />

          <div v-else>
            <div class="mb-8 font-bold desktop:mt-2">
              {{ $t('house.reviews.allReviews') }}
            </div>
            <div class="desktop:max-h-[400px]">
              <HouseCustomerReview
                v-for="(review, index) in reviews"
                :key="`customer-review-modal-${index}`"
                :active-truncate="false"
                :review="review"
                class="mb-6 border-b border-gray-200 pb-6 last:border-0"
              />
            </div>
          </div>
        </div>
      </template>
    </BaseModalV2>
  </div>
</template>
